import { useState } from 'react'
import {
  STAFF_LEVEL_LABELS,
  levelsAbove,
  starStateWords,
  starsForStep,
  type SupportStarLevel,
  type SupportStars,
} from '../lib/supportStars'

/**
 * 172. Stars for a platform ticket, in the staff panel under Developers.
 *
 * ENGLISH ON PURPOSE, like the rest of the panel (SHL-25). Props-only.
 * The state line is drawn for anybody staffing the ticket; the buttons only
 * when the server said `canAward` (a platform holder, not the asker) AND the
 * route wired `onAward` — rule 4: no door that refuses.
 *
 * A press asks once ("pay N stars?") because the act pays real stars and is
 * not undone; a raise names the DIFFERENCE, which is what it pays.
 */
export function SupportStarAward({
  stars,
  onAward,
  busy = false,
}: {
  stars: SupportStars
  onAward?: (level: SupportStarLevel) => void
  busy?: boolean
}) {
  const [asking, setAsking] = useState<SupportStarLevel | null>(null)
  const above = levelsAbove(stars.level)
  const canPress = !!onAward && stars.canAward && !!stars.levels && above.length > 0

  return (
    <>
      <h4 className="supportstaff__sub">Stars for this ticket</h4>
      <p className="supportstaff__state" aria-live="polite">{starStateWords(stars)}</p>
      {canPress && stars.levels && !asking && (
        <div className="supportstaff__actions">
          {above.map((l) => (
            <button key={l} type="button" className="btn btn--sm" onClick={() => setAsking(l)} disabled={busy}>
              {STAFF_LEVEL_LABELS[l]} · {stars.level ? '+' : ''}{starsForStep(stars.levels!, stars.level, l).toLocaleString()}
            </button>
          ))}
        </div>
      )}
      {canPress && stars.levels && asking && (
        <div className="supportstaff__form">
          <p className="supportstaff__warn">
            Pay the person who asked {starsForStep(stars.levels, stars.level, asking).toLocaleString()} stars for
            “{STAFF_LEVEL_LABELS[asking]}”? They are told on their bell. A ticket can be raised later; it never goes down.
          </p>
          <div className="supportstaff__actions">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={busy}
              onClick={() => {
                onAward!(asking)
                setAsking(null)
              }}
            >
              Award stars
            </button>
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => setAsking(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {!stars.canAward && stars.level === null && (
        <p className="supportstaff__empty">Only Platform support holders award stars, and never on their own ticket.</p>
      )}
    </>
  )
}
